import { Injectable } from '@angular/core';
import { ApiService } from '@core/api.service';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/internal/operators';
import { LocalStorageService } from '@app/core/local-storage/local-storage.service';

const cachedKeys = {
  // 'tecdoc_options': true,
  // 'id_token': true,
  'cached-list': true,
  'cached-groups': true,
  'cached-user-deliveries': true,
  'cached-user-pickup-delivery-options': true,
  'cached-user-deliveries-by-order': true,
};
type CachedKeys = keyof typeof cachedKeys;
const cachedKeysList: CachedKeys[] = Object.keys(cachedKeys) as CachedKeys[];
const expirationSuffix = '_expiration';
@Injectable()
export class CachedRequestService {
  private plateNumberStatus = new BehaviorSubject<any>({found: false, reload: false, state: 0, value: ''});

  constructor(
    private apiService: ApiService,
    private localStorageService: LocalStorageService
  ) {}

  get plateNumberStatus$() {
    return this.plateNumberStatus.asObservable();
  }

  private static toExpirationKeyTyped(key: CachedKeys): string {
    return key + expirationSuffix;
  }

  private static parseExpirationMs(value: any): number | null {
    if (value == null) {
      return null;
    }
    if (typeof value === 'number' && isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      // Accept either ISO strings or numeric timestamps.
      const asNum = Number(value);
      if (isFinite(asNum) && value.trim() !== '') {
        return asNum;
      }
      const parsed = Date.parse(value);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  hasKey(key: CachedKeys): boolean {
    this.checkForKey(key);
    // If expiration exists and is expired -> purge and return false.
    if (this.isExpired(key)) {
      this.clearItem(key);
      return false;
    }
    // Expiration is optional for hasKey (non-strict).
    return this.localStorageService.hasKey(key);
  }

  hasKeyAndNotExpired(key: CachedKeys): boolean {
    this.checkForKey(key);
    // Expiration is required here. Missing expiration => treat as expired.
    if (!this.localStorageService.hasKey(CachedRequestService.toExpirationKeyTyped(key))) {
      this.clearItem(key);
      return false;
    }
    if (this.isExpired(key)) {
      this.clearItem(key);
      return false;
    }
    return this.localStorageService.hasKey(key);
  }

  getItem(key: CachedKeys) {
    this.checkForKey(key);
    if (this.isExpired(key)) {
      this.clearItem(key);
      return null;
    }
    return this.localStorageService.getItem(key);
  }

  setItem(key: CachedKeys, value: any, expiration: number | string | Date = null) {
    this.checkForKey(key);
    this.localStorageService.setItem(key, value);

    const expirationKey = CachedRequestService.toExpirationKeyTyped(key);
    if (expiration == null) {
      // No expiration provided: remove any existing expiration to avoid stale TTL rules.
      this.localStorageService.clearItem(expirationKey);
      return;
    }
    this.localStorageService.setItem(expirationKey, expiration);
  }

  clearItem(key: CachedKeys) {
    this.checkForKey(key);
    const expirationKey = CachedRequestService.toExpirationKeyTyped(key);
    this.localStorageService.clearItem(expirationKey);
    return this.localStorageService.clearItem(key);
  }
  checkForKey(key: CachedKeys) {
    if (!cachedKeys[key]) {
      throw new Error('CACHE_KEY_ERROR');
    }
  }

  clearItems() {
    const now = Date.now();
    cachedKeysList.forEach((key) => {
      const expirationKey = CachedRequestService.toExpirationKeyTyped(key);
      // Only remove items that have expiration and are already expired.
      if (!this.localStorageService.hasKey(expirationKey)) {
        this.clearItem(key);
        return;
      }

      const expirationValue = this.localStorageService.getItem(expirationKey);
      const expirationMs = CachedRequestService.parseExpirationMs(expirationValue);
      if (expirationMs == null || expirationMs >= now) {
        return;
      }

      this.clearItem(key);
    });
  }

  private isExpired(key: CachedKeys): boolean {
    const expirationKey = CachedRequestService.toExpirationKeyTyped(key);
    if (!this.localStorageService.hasKey(expirationKey)) {
      return false;
    }
    const expirationValue = this.localStorageService.getItem(expirationKey);
    const expirationMs = CachedRequestService.parseExpirationMs(expirationValue);
    if (expirationMs == null) {
      return false;
    }
    return expirationMs < Date.now();
  }
}
